/**
 * Reiner Rechner für den Storno mit Entscheidung je Posten (ADR 0022). Keine DB, keine Seiteneffekte;
 * Server (Verrechnung in der Storno-Transaktion) und Client (Vorschau im Dialog) rechnen damit gleich.
 * Typen als Strings, damit die Datei ohne TypeORM-Entities im Client läuft.
 */

/** Was mit dem Rest des bezahlten Geldes nach der Verrechnung geschieht (ADR 0022). */
export enum CancelRestHandling {
    /** Rest wird Guthaben des Buchers (Vorauswahl im Dialog). */
    Credit = "Credit",
    /** Rest wurde außerhalb der App erstattet (Überweisung, bar); nur Vermerk. */
    ManualRefund = "ManualRefund",
    /** Rest bleibt bei der Schule (Stornobedingungen); nur Vermerk. */
    KeptBySchool = "KeptBySchool",
    /** Rest ist vor dem Storno über Mollie erstattet worden (Client ruft den Refund vorher auf). */
    MollieRefund = "MollieRefund",
}

export const CANCEL_REST_LABELS: Record<CancelRestHandling, string> = {
    [CancelRestHandling.Credit]: "als Guthaben",
    [CancelRestHandling.ManualRefund]: "per Überweisung erstattet",
    [CancelRestHandling.KeptBySchool]: "bleibt bei der Schule",
    [CancelRestHandling.MollieRefund]: "über Mollie erstattet",
};

export const CANCEL_FEE_TYPES: ReadonlyArray<string> = ["DunningLevel1", "DunningLevel2", "DunningLevel3", "CancelationFee", "Chargeback"];

export type CancelPlanPost = {
    id: string;
    type: string;
    paymentAmount: number;
    openAmount?: number | null;
    paymentDone?: Date | string | null;
    refundedAmount?: number | null;
    deletedAt?: Date | string | null;
    /** Tatsächlich bei Mollie gezahlt (kann unter paymentAmount liegen, wenn die Rate nach der Zahlung erhöht wurde). */
    molliePaidAmount?: number | null;
};

export type CancelFeeDecision = {scheduledPaymentId: string; keep: boolean};

export type CancelSettlementPlan = {
    /** Offene Gebühren, die bleiben (Merker), mit ihrem offenen Betrag. */
    keptFees: Array<{id: string; open: number}>;
    /** Offene Gebühren, die erlassen werden. */
    waivedFees: Array<{id: string; open: number; paid: number}>;
    /** Neue Stornogebühr (0 = keine). */
    cancelationFee: number;
    /** Bezahltes Geld auf Kursraten und Zusatzposten abzüglich bereits erstatteter Beträge. */
    available: number;
    /** Summe aller bleibenden offenen Gebühren einschließlich der neuen Stornogebühr. */
    feesOpen: number;
    /** Betrag, der aus dem bezahlten Geld auf die bleibenden Gebühren getilgt wird. */
    settleAmount: number;
    /** Rest des bezahlten Geldes nach der Verrechnung. */
    rest: number;
    /** Gebühren, die nach der Verrechnung offen bleiben (feesOpen − settleAmount); Dialog und Historie nennen diesen Betrag, die Mail rechnet aus dem gespeicherten Stand. */
    openAfter: number;
};

const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;
const effectiveOpen = (post: CancelPlanPost): number => round2(Number(post.openAmount ?? post.paymentAmount));

export const isCancelFeeType = (type: string | null | undefined): boolean => type != null && CANCEL_FEE_TYPES.indexOf(type) !== -1;

/** Bezahlter Anteil eines Postens abzüglich Erstattungen; nie negativ. */
export const paidAmountOf = (post: CancelPlanPost): number => {
    const amount = Number(post.paymentAmount);
    // Bezahlte Mollie-Rate: Basis ist das wirklich eingegangene Geld (wie getRefundCap), nicht der Nennbetrag,
    // in beide Richtungen (nach der Zahlung erhöhte Rate zählt weniger, Überzahlung nach Senkung zählt mehr).
    const paid = post.paymentDone ? (post.molliePaidAmount != null ? Number(post.molliePaidAmount) : amount) : Math.max(0, amount - effectiveOpen(post));

    return round2(Math.max(0, paid - Number(post.refundedAmount ?? 0)));
};

export const isOpenFee = (post: CancelPlanPost): boolean => !post.deletedAt && isCancelFeeType(post.type) && !post.paymentDone && effectiveOpen(post) > 0;

/** Kursraten und Zusatzposten (alles außer Gebühren und Rabattzeilen). */
export const isCoursePost = (post: CancelPlanPost): boolean => !post.deletedAt && post.type !== "Discount" && !isCancelFeeType(post.type);

export const planCancelSettlement = (input: {
    posts: Array<CancelPlanPost>;
    decisions?: Array<CancelFeeDecision> | null;
    cancelationFeeAmount?: number | null;
    settleFeesFromPayments?: boolean | null;
}): CancelSettlementPlan => {
    const active = input.posts.filter((post) => !post.deletedAt);
    const decisionById = new Map((input.decisions ?? []).map((decision) => [decision.scheduledPaymentId, decision.keep]));
    const openFees = active.filter(isOpenFee);
    // Fehlt eine Entscheidung, gilt die Vorauswahl „bleibt offen“.
    const keptFees = openFees.filter((fee) => decisionById.get(fee.id) !== false).map((fee) => ({id: fee.id, open: effectiveOpen(fee)}));
    const waivedFees = openFees.filter((fee) => decisionById.get(fee.id) === false).map((fee) => ({id: fee.id, open: effectiveOpen(fee), paid: paidAmountOf(fee)}));
    const cancelationFee = round2(Math.max(0, Number(input.cancelationFeeAmount ?? 0)));
    const available = round2(active.filter(isCoursePost).reduce((sum, post) => sum + paidAmountOf(post), 0));
    const feesOpen = round2(keptFees.reduce((sum, fee) => sum + fee.open, 0) + cancelationFee);
    const settleAmount = input.settleFeesFromPayments === false ? 0 : round2(Math.min(available, feesOpen));

    return {keptFees, waivedFees, cancelationFee, available, feesOpen, settleAmount, rest: round2(available - settleAmount), openAfter: round2(feesOpen - settleAmount)};
};
